import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { CheckIcon, PencilSimpleIcon, XIcon } from '@phosphor-icons/react';

import { UpdateUserDto, useUpdateUser } from '../api/admin-api';

import { adminButtonSx, adminColors } from './adminTheme';

/** Normalized user shape, so this modal works from both the tenant members tab
 * (AdminTenantMember) and the global users page (AdminUserListItem). */
export interface EditableUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  enabled: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  user: EditableUser;
}

/** Modal that edits an existing realm user's profile. */
export const EditUserModal: FC<Props> = observer(({ open, onClose, user }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const btn = adminButtonSx(theme);
  const { t } = useTrans();
  const update = useUpdateUser();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setEmail(user.email || '');
    setEnabled(user.enabled);
    setSubmitted(false);
  }, [open, user]);

  const firstNameError = submitted && !firstName.trim();
  const lastNameError = submitted && !lastName.trim();
  const emailError = submitted && !email.trim();

  const handleSubmit = () => {
    setSubmitted(true);
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return;
    const body: UpdateUserDto = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      enabled,
    };
    update.mutate({ userId: user.id, body }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 5, overflow: 'hidden' } }}>
      <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.625, borderBottom: `1px solid ${c.border}` }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, backgroundColor: theme.palette.grey[200], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PencilSimpleIcon size={20} color={c.azure} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: c.ink }}>
            {t('app.admin.editUser.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.faint, mt: 0.125 }}>
            {t('app.admin.editUser.subtitle')}
          </Typography>
        </Box>
        <Button onClick={onClose} sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}>
          <XIcon size={16} />
        </Button>
      </Box>

      <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField
            label={t('app.admin.editUser.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={firstNameError}
            size="small"
            fullWidth
          />
          <TextField
            label={t('app.admin.editUser.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={lastNameError}
            size="small"
            fullWidth
          />
        </Stack>
        <TextField
          label={t('app.admin.editUser.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailError}
          size="small"
          fullWidth
        />
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={
            <Typography sx={{ fontSize: 13, color: c.ink }}>
              {t('app.admin.editUser.enabled')}
            </Typography>
          }
        />
      </Box>

      <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25, borderTop: `1px solid ${c.border}` }}>
        <Button onClick={onClose} sx={{ ...btn.neutral, px: 2, borderRadius: 2.75 }}>
          {t('app.admin.editUser.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={update.isPending}
          startIcon={<CheckIcon size={16} />}
          sx={{ ...btn.solid, px: 2, borderRadius: 2.75 }}
        >
          {t('app.admin.editUser.submit')}
        </Button>
      </Box>
    </Dialog>
  );
});
